import { computed, ComputedRef, Ref, ref } from '@nuxtjs/composition-api';
import useCartCookie from '@shared/composables/useCartCookie';
import { IAutomaticDiscount } from '@shared/types/IAutomaticDiscount';
import { cartGetters } from '@unified-commerce/gpc-vue-storefront-shopify';
import { useAutomaticDiscounts } from '@unified-commerce/gpc-vue-storefront-shopify-admin';
import debounce from 'lodash/debounce';

import { hiddenProductsToRemove, updateDiscountProgress } from './helpers';

export interface DiscountProgress {
  discount: IAutomaticDiscount;
  counter: number;
  nextRewardLevel: number;
  numberGiftsEligibleFor: number;
}

const discountProgress = ref<DiscountProgress[]>([]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Cart = any;

interface UseShopifyDiscounts {
  discountProgress: Ref<DiscountProgress[]>;
  hasUnclaimedGifts: ComputedRef<boolean>;
  load: () => Promise<void>;
  updateCartDiscountProgress: (cart: Cart) => Promise<void>;
  loading: ComputedRef<boolean>;
  error: ComputedRef<Error | null>;
}

const useShopifyDiscounts = (): UseShopifyDiscounts => {
  const { result, loading, error, getAutomaticDiscounts } = useAutomaticDiscounts(`free-gift`);
  const { loading: cartLoading, removeItem } = useCartCookie();

  const load = async () => {
    if (loading.value || error.value) return;
    await getAutomaticDiscounts();
    discountProgress.value = (result.value ?? []).map((discount: IAutomaticDiscount) => ({
      discount,
      counter: 0,
      numberGiftsEligibleFor: 0,
      nextRewardLevel: discount.numberItemsRequired,
    }));
  };

  const removeProduct = debounce(async ({ product }) => {
    if (cartLoading.value) return;
    await removeItem({ product });
  }, 500);

  const hasUnclaimedGifts = computed(() =>
    discountProgress.value.some(
      ({ numberGiftsEligibleFor }: DiscountProgress) => numberGiftsEligibleFor > 0,
    ),
  );

  const updateCartDiscountProgress = async (cart: Cart) => {
    const lineItems = cartGetters.getItems(cart);
    if (!lineItems?.length || loading.value || error.value) return;
    updateDiscountProgress(discountProgress.value, lineItems);
    const productsToRemove = hiddenProductsToRemove(discountProgress.value, lineItems);
    if (productsToRemove.length) {
      await Promise.all(productsToRemove.map(removeProduct));
    }
  };

  return {
    discountProgress,
    hasUnclaimedGifts,
    load,
    updateCartDiscountProgress,
    loading,
    error,
  };
};

export default useShopifyDiscounts;
